import { useId, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useSheetFocus } from '@/lib/use-sheet-focus'

const dragCloseThreshold = 90

export function BottomSheet({
  children,
  onClose,
  open,
  title,
  titleVisuallyHidden = false,
}: {
  children: ReactNode
  onClose: () => void
  open: boolean
  title: string
  /** 标题只供无障碍朗读(如内容区已展示同名标题),视觉上看不到标题行。 */
  titleVisuallyHidden?: boolean
}) {
  const { t } = useTranslation('shell')
  const titleId = useId()
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const dragStartYRef = useRef<number | null>(null)
  const [dragOffset, setDragOffset] = useState(0)

  const closeSheet = () => {
    dragStartYRef.current = null
    setDragOffset(0)
    onClose()
  }

  useSheetFocus(sheetRef, open, closeSheet)

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    dragStartYRef.current = event.clientY
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStartYRef.current === null) {
      return
    }

    setDragOffset(Math.max(0, event.clientY - dragStartYRef.current))
  }

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStartYRef.current === null) {
      return
    }

    const movedDown = event.clientY - dragStartYRef.current > dragCloseThreshold

    dragStartYRef.current = null
    setDragOffset(0)

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    if (movedDown) {
      closeSheet()
    }
  }

  // portal 到 body:sheet 使用 fixed 定位,若留在带 transform 的祖先(抽屉滑动的
  // main、居中 composer 的 -translate-y 容器)内,fixed 会以该祖先为包含块而错位。
  return createPortal(
    <div
      aria-hidden={!open}
      className={open ? 'pointer-events-auto' : 'pointer-events-none'}
      data-testid="bottom-sheet-root"
    >
      <button
        aria-label={t('sheet.close')}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
        disabled={!open}
        onClick={closeSheet}
        tabIndex={-1}
        type="button"
      />
      <div
        ref={sheetRef}
        aria-labelledby={titleId}
        aria-modal={open ? 'true' : undefined}
        className={`fixed inset-x-0 bottom-0 z-50 max-h-[calc(100dvh-52px)] overflow-hidden rounded-t-2xl bg-white transition-transform duration-[280ms] [transition-timing-function:cubic-bezier(.22,.7,.3,1)] ${open ? 'translate-y-0 shadow-[0_-12px_36px_rgba(26,28,25,0.16)]' : 'translate-y-full shadow-none'}`}
        inert={!open ? true : undefined}
        role="dialog"
        style={dragOffset > 0 ? { transform: `translateY(${String(dragOffset)}px)` } : undefined}
      >
        <div
          className="touch-none px-4 pt-2"
          onPointerCancel={handlePointerEnd}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
        >
          <div aria-hidden className="mx-auto h-1 w-10 rounded-full bg-[#d0d5dd]" />
          <div className="grid min-h-11 grid-cols-[minmax(0,1fr)_44px] items-center">
            <h2
              className={
                titleVisuallyHidden
                  ? 'sr-only'
                  : 'truncate text-[15px] leading-5 font-semibold text-[var(--color-text)]'
              }
              id={titleId}
            >
              {title}
            </h2>
            <button
              aria-label={t('sheet.close')}
              className="flex size-11 items-center justify-center rounded-lg text-2xl leading-none text-[var(--color-text-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
              data-sheet-initial-focus
              onClick={closeSheet}
              type="button"
            >
              <span aria-hidden>×</span>
            </button>
          </div>
        </div>
        <div className="max-h-[calc(100dvh-108px)] overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
